/**
 * Shared `queryOptions` for TanStack Query.
 *
 * As always, you can add a query-options.ts file to your component's directory,
 * but if your query is used by more than one component put it in this file.
 *
 * See https://tanstack.com/query/latest/docs/framework/react/guides/query-options
 *
 * Usage Notes
 * ============
 *
 * - Name the options function after the request, like `caseGet` > `caseGetOptions`.
 * - Also name the query key after the request, like `caseGet` > `["caseGet", ...]`.
 * - Set a long `staleTime` for requests that we don't update often to avoid unnecessary re-fetches.
 *   E.g. the `getFirm` request could have `staleTime: Infinity`. If we update the firm, we can
 *   refetch everywhere the firm data is used with `queryClient.invalidateQueries(firmGetOptions().queryKey)`.
 * - Don't be shy about duplicate requests in components! As long as you're fetching within the
 *   `staleTime` window, react-query will deduplicate the requests. You do not have to pass data down
 *   to multiple layers of child components, just get the data where you need it.
 *
 *
 * Example
 * ============
 *
 * #### src/lib/query-options.ts
 * ```tsx
 * import { useFirmApi } from "@/hooks/useApi";
 *
 * const firmApi = useFirmApi();
 *
 * function firmGetOptions(firmId: string) {
 *   return {
 *     queryKey: ["firm", firmId],
 *     queryFn: () => firmApi.firmGet({ id: firmId }),
 *   };
 * }
 * ```
 *
 * #### src/components/MyComponent/MyComponent.tsx
 * ```tsx
 * const firmGetQuery = useQuery(firmGetOptions("123"));
 * ```
 *
 */

import { queryOptions } from "@tanstack/react-query";
import {
  type CaseApi,
  type CaseFileApi,
  type CaseFileType,
  type ClientApi,
  type CommunicationApi,
  CommunicationStatus,
  type CredentialApi,
  type DefinitionApi,
  type DocumentApi,
  type DocumentStatus,
  type DocumentType,
  type FirmApi,
  FlaggedItemObjectType,
  IncludedDocuments,
  type ObjectionApi,
  ObjectTypeEnum,
  type ProductionSetApi,
  Role,
  type UserApi,
  type UserNotificationApi,
} from "@/briefpoint-client";
import type { UserResponse } from "./types";

// AKA "Production Files"
export const getCaseFilesOptions = ({
  caseFileApi,
  caseId,
  fileType,
  status,
  pageSize,
  page,
  checkForResponsiveFiles,
}: {
  caseFileApi: CaseFileApi;
  caseId: string;
  fileType: CaseFileType;
  status?: DocumentStatus;
  pageSize?: number;
  page?: number;
  checkForResponsiveFiles?: boolean;
}) =>
  queryOptions({
    // queryKey is filtered to remove undefined values, so if you want queryKey
    // matching to work, make sure you don't leave off anything in between.
    queryKey: [
      "caseFileApi.caseFileGetCaseFiles",
      caseId,
      fileType,
      status,
      pageSize,
      page,
      checkForResponsiveFiles,
    ].filter(Boolean),
    queryFn: () => {
      if (!caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileGetCaseFiles({
        caseId: caseId,
        fileType: fileType,
        status,
        pageSize: pageSize,
        page: page,
        checkForResponsiveFiles: checkForResponsiveFiles ?? false,
      });
    },
  });

export const getCaseOptions = (
  caseApi: CaseApi,
  firmId: string | undefined,
  caseId: string | undefined,
) =>
  queryOptions({
    queryKey: ["caseApi.caseGet", firmId, caseId],
    queryFn: () => {
      if (!firmId || !caseId) {
        throw new Error("Firm ID and Case ID are required");
      }
      return caseApi.caseGet({ firmId, id: caseId });
    },
    enabled: !!firmId && !!caseId,
  });

export const getCaseFilesWithheldListOptions = ({
  caseFileApi,
  caseId,
}: {
  caseFileApi: CaseFileApi;
  caseId: string;
}) =>
  queryOptions({
    queryKey: ["caseFileApi.caseFileGetWithheldList", caseId].filter(Boolean),
    queryFn: () => {
      if (!caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileGetWithheldList({
        caseId: caseId,
      });
    },
  });

export const getCaseFileLatestBatchOptions = (
  caseFileApi: CaseFileApi,
  caseId?: string,
) =>
  queryOptions({
    // Note this endpoint is polled in CaseProduction, so we need to add a unique identifier if this is reused.
    queryKey: ["caseFileApi.caseFileGetCaseFileLatestBatch", caseId],
    queryFn: () => {
      if (!caseId) throw new Error("Case ID is required");

      return caseFileApi.caseFileGetCaseFileLatestBatch({
        caseId,
      });
    },
  });

export const getPurgatorySummaryOptions = (
  caseFileApi: CaseFileApi,
  caseId: string,
) =>
  queryOptions({
    queryKey: ["caseFileApi.caseFileGetPurgatorySummary", caseId],
    queryFn: () => caseFileApi.caseFileGetPurgatorySummary({ caseId }),
  });

export const getLatestQueryForInterrogatoryOptions = (
  documentId: string,
  interrogatoryId: number,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetLatestQueryForInterrogatory",
      documentId,
      interrogatoryId,
    ],
    queryFn: () =>
      documentApi.documentGetLatestQueryForInterrogatory({
        documentId,
        interrogatoryId,
      }),
  });

export const getProductionSetOptions = (
  documentId: string,
  productionSetApi: ProductionSetApi,
  useDocHandling = false,
) =>
  queryOptions({
    queryKey: ["productionSetApi.productionSetGetForDocument", documentId],
    queryFn: () => productionSetApi.productionSetGetForDocument({ documentId }),
    enabled: useDocHandling,
  });

export const getReadUrlOptions = (
  fileId: string,
  caseId: string,
  caseFileApi: CaseFileApi,
) =>
  queryOptions({
    queryKey: ["caseFileApi.caseFileGetReadUrl", fileId, caseId],
    enabled: !!fileId,
    queryFn: async () => {
      const data = await caseFileApi.caseFileGetReadUrl({
        fileId,
        caseId,
      });
      return JSON.parse(data);
    },
  });

// client uploads for case+client (no firmId; may have different auth than bridge endpoint)
export const getClientUploadsOptions = ({
  caseFileApi,
  caseId,
  clientId,
}: {
  caseFileApi: CaseFileApi;
  caseId: string;
  clientId: string;
}) =>
  queryOptions({
    queryKey: ["caseFileApi.caseFileGetClientUploads", caseId, clientId],
    queryFn: () =>
      caseFileApi.caseFileGetClientUploads({
        caseId,
        clientId,
      }),
    enabled: !!caseId && !!clientId,
  });

export const getResponsiveFilesForInterrogatoryOptions = (
  documentId: string,
  interrogatoryId: number,
  documentApi: DocumentApi,
  filterWithheld = false,
  useDocHandling = false,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetResponsiveFilesForInterrogatory",
      documentId,
      interrogatoryId,
    ],
    queryFn: () =>
      documentApi.documentGetResponsiveFilesForInterrogatory({
        documentId,
        interrogatoryId,
      }),
    select: (data) => {
      if (filterWithheld) {
        return data.filter((file) => !file.isWithheld);
      }
      return data;
    },
    enabled: useDocHandling,
  });

export const responsiveFileMapOptions = (
  documentId: string,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetResponsiveFileMapForCaseInterrogatories",
      documentId,
    ],
    queryFn: () =>
      documentApi.documentGetResponsiveFileMapForCaseInterrogatories({
        documentId,
      }),
    enabled: !!documentId,
  });

export const searchCaseFilesQueryOptions = (
  caseFileApi: CaseFileApi,
  {
    caseId,
    folderId,
    search,
  }: {
    caseId?: string;
    folderId?: string;
    search?: string;
  },
) =>
  queryOptions({
    queryKey: [
      "caseFileApi.caseFileSearchCaseFiles",
      caseId,
      ...(folderId !== undefined ? [folderId] : []),
      ...(search !== undefined ? [search] : []),
    ],
    queryFn: () => {
      if (!caseId) throw new Error("Case ID is required");
      return caseFileApi.caseFileSearchCaseFiles({ caseId, folderId, search });
    },
  });

export const getInterrogatoryOptions = (
  documentId: string,
  interrogatoryId: number,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetInterrogatory",
      documentId,
      interrogatoryId,
    ],
    queryFn: () =>
      documentApi.documentGetInterrogatory({ documentId, interrogatoryId }),
    enabled: !!documentId && !!interrogatoryId,
    staleTime: 0,
  });

export const getDocumentOptions = (
  documentId: string,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: ["documentApi.documentGet", documentId],
    queryFn: () => {
      if (!documentId) {
        throw new Error("Document ID is required");
      }
      return documentApi.documentGet({ documentId });
    },
  });

export const getSimilarRequestsForInterrogatoryOptions = (
  documentId: string,
  interrogatoryId: number,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentFindSimilarRequestsForInterrogatory",
      documentId,
      interrogatoryId,
    ],
    queryFn: () =>
      documentApi.documentFindSimilarRequestsForInterrogatory({
        documentId,
        interrogatoryId,
      }),
    enabled: !!documentId && interrogatoryId != null,
  });

export const caseGetOptions = (
  caseApi: CaseApi,
  firmId: string | undefined,
  caseId: string | undefined,
) =>
  queryOptions({
    queryKey: ["caseApi.caseGet", firmId, caseId],
    queryFn: () => {
      if (!firmId || !caseId) {
        throw new Error("Firm ID and Case ID are required");
      }
      return caseApi.caseGet({ firmId, id: caseId });
    },
    enabled: !!firmId && !!caseId,
  });

export const getObjectionsOptions = (
  objectionApi: ObjectionApi,
  documentType?: DocumentType,
) =>
  queryOptions({
    queryKey: ["objectionApi.objectionGetList", documentType],
    queryFn: () => objectionApi.objectionGetList({ documentType }),
  });

export const getFormInterrogatoriesOptions = (objectionApi: ObjectionApi) =>
  queryOptions({
    queryKey: ["objectionApi.objectionGetFormInts"],
    queryFn: () => objectionApi.objectionGetFormInts(),
  });

export const getDefinitionsOptions = (definitionApi: DefinitionApi) =>
  queryOptions({
    queryKey: ["definitionApi.definitionGetList"],
    queryFn: () => definitionApi.definitionGetList({}),
  });

export const getArchivedDocumentListOptions = (documentApi: DocumentApi) =>
  queryOptions({
    queryKey: ["documentApi.documentGetList", IncludedDocuments.Archived],
    queryFn: () =>
      documentApi.documentGetList({
        includedDocuments: IncludedDocuments.Archived,
      }),
  });

export const getUnarchivedDocumentListOptions = (documentApi: DocumentApi) =>
  queryOptions({
    queryKey: ["documentApi.documentGetList", IncludedDocuments.NonArchived],
    queryFn: () =>
      documentApi.documentGetList({
        includedDocuments: IncludedDocuments.NonArchived,
      }),
  });

export const getUnarchivedCaseDocumentListOptions = (
  documentApi: DocumentApi,
  caseId?: string,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetCaseDocuments",
      IncludedDocuments.NonArchived,
      ...(caseId !== undefined ? [caseId] : []),
    ],
    queryFn: () =>
      documentApi.documentGetCaseDocuments({
        caseId: caseId ?? "",
        includedDocuments: IncludedDocuments.NonArchived,
      }),
  });

export const getProcessingDocumentsOptions = (documentApi: DocumentApi) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetList",
      IncludedDocuments.NonArchived,
      "processing",
    ],
    queryFn: () =>
      documentApi.documentGetList({
        includedDocuments: IncludedDocuments.NonArchived,
      }),
  });

export const getProcessingCaseDocumentsOptions = (
  documentApi: DocumentApi,
  caseId: string,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetList",
      IncludedDocuments.NonArchived,
      caseId,
      "processing",
    ],
    refetchOnWindowFocus: false,
    queryFn: () =>
      documentApi.documentGetCaseDocuments({
        caseId,
        includedDocuments: IncludedDocuments.NonArchived,
      }),
  });

export const getFirmCommunicationsOptions = (
  communicationApi: CommunicationApi,
  firmId: string,
) =>
  queryOptions({
    queryKey: ["communicationApi.communicationGetFirmCommunications", firmId],
    queryFn: async () => {
      return await communicationApi.communicationGetFirmCommunications({
        lowerBound: CommunicationStatus.Pending,
        upperBound: CommunicationStatus.Complete,
      });
    },
  });

export const getClientsOptions = (
  clientApi: ClientApi,
  user: UserResponse | undefined,
) =>
  queryOptions({
    queryKey: ["clientApi.clientGetAll", user?.firmId],
    queryFn: async () => {
      return await clientApi.clientGetAll({
        firmId: user?.firmId ?? "",
        usersClientsOnly: !user?.role?.includes(Role.FirmAdmin),
      });
    },
  });

export const getCommunicationsForObjectOptions = (
  communicationApi: CommunicationApi,
  objectId: string | undefined,
  type: ObjectTypeEnum = ObjectTypeEnum.Document,
) =>
  queryOptions({
    queryKey: [
      "communicationApi.communicationGetCommunicationsForObject",
      { objectId, type },
    ],
    queryFn: () => {
      if (!objectId) {
        throw new Error("Object ID is required");
      }
      return communicationApi.communicationGetCommunicationsForObject({
        objectId,
        type,
      });
    },
    enabled: !!objectId,
  });

export const getCredentialOptions = (
  credentialApi: CredentialApi,
  clientId: string,
) =>
  queryOptions({
    queryKey: ["credentialApi.credentialCreate", clientId],
    queryFn: () => credentialApi.credentialCreate({ clientId }),
  });

export const clientGetOptions = (
  clientApi: ClientApi,
  firmId: string | undefined,
  clientId: string | undefined,
) =>
  queryOptions({
    queryKey: ["clientApi.clientGet", firmId, clientId],
    queryFn: () => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      if (!firmId) {
        throw new Error("Firm ID is required");
      }
      return clientApi.clientGet({ firmId, id: clientId });
    },
    enabled: !!firmId && !!clientId,
  });

export const getSupplementalInterrogatoryResponsesOptions = (
  documentId: string,
  interrogatoryId: number,
  documentApi: DocumentApi,
) =>
  queryOptions({
    queryKey: [
      "documentApi.documentGetSupplementalInterrogatoryResponses",
      documentId,
      interrogatoryId,
    ],
    queryFn: () =>
      documentApi.documentGetSupplementalInterrogatoryResponses({
        documentId,
        interrogatoryId,
      }),
    enabled: !!documentId && !!interrogatoryId,
  });

export const getFirmSubscriptionOptions = (firmApi: FirmApi, firmId: string) =>
  queryOptions({
    queryKey: ["firmApi.firmGet", firmId],
    queryFn: () => firmApi.firmGet({ id: firmId }),
  });

export const getFirmCasesOptions = (firmApi: FirmApi, firmId: string) =>
  queryOptions({
    queryKey: ["firmApi.firmGetFirmCases", firmId],
    queryFn: () => firmApi.firmGetFirmCases({ firmId }),
  });

/* used when we only need to get the bar numbers for a user, not the entire user object */
export const getBarNumbersOptions = (userApi: UserApi, userId: string) =>
  queryOptions({
    queryKey: ["userApi.userGet", userId, "barNumbers"],
    queryFn: () => userApi.userGetUser({ userId }),
  });

export const getUserNotificationsOptions = (
  userNotificationApi: UserNotificationApi,
) =>
  queryOptions({
    queryKey: [
      "userNotificationApi.userNotificationGetUserNotificationsByFirmId",
    ],
    queryFn: () =>
      userNotificationApi.userNotificationGetUserNotificationsByFirmId({
        flaggedItemType: FlaggedItemObjectType.Interrogatory,
      }),
  });

export const getUserNotificationHistoryOptions = (
  userNotificationApi: UserNotificationApi,
  interrogatoryId: number,
) =>
  queryOptions({
    queryKey: [
      "userNotificationApi.userNotificationGetUserNotificationsDetailByFlaggedItem",
      interrogatoryId,
    ],
    queryFn: () =>
      userNotificationApi.userNotificationGetUserNotificationsDetailByFlaggedItem(
        {
          flaggedItemId: interrogatoryId.toString(),
        },
      ),
  });
